import React from "react";
import { ArrowUpRight, Play } from "lucide-react";
import { FEATURED_PROJECT, PROFILE } from "@/lib/portfolioData";
import SectionHeader from "@/components/SectionHeader";
import Reveal from "@/components/Reveal";

export default function FeaturedProject() {
  const p = FEATURED_PROJECT;
  return (
    <section
      id="featured"
      className="relative py-24 md:py-36 px-6 md:px-10"
      data-testid="featured-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          number="05"
          tag="Case Study"
          title={<>
            Featured <span className="text-gradient-gold">Project</span>
          </>}
        />

        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Cover */}
            <div className="lg:col-span-7 relative">
              <div className="arched relative aspect-[4/5] md:aspect-[5/6] group overflow-hidden">
                <img
                  src={p.cover}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <a
                  href={PROFILE.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute top-6 right-6 w-14 h-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center hover:scale-110 transition-transform"
                  data-testid="featured-play"
                  aria-label="Watch on Instagram"
                >
                  <Play className="w-5 h-5" fill="currentColor" />
                </a>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2">
                    {p.category}
                  </p>
                  <h3
                    className="font-display font-bold uppercase text-white leading-[0.95]"
                    style={{ fontSize: "clamp(1.5rem, 3vw, 2.75rem)" }}
                  >
                    {p.title}
                  </h3>
                </div>
              </div>

              {/* Gallery strip */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                {p.gallery.map((g, i) => (
                  <div key={i} className="aspect-[4/3] rounded-lg overflow-hidden border border-white/5">
                    <img
                      src={g}
                      alt={`Gallery ${i + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Details */}
            <div className="lg:col-span-5 lg:pt-8">
              <p className="text-white/85 text-base md:text-lg leading-relaxed mb-10">
                {p.description}
              </p>

              {/* Metrics */}
              <div className="grid grid-cols-2 gap-3 mb-10">
                {p.metrics.map((m) => (
                  <div
                    key={m.label}
                    className="glass rounded-xl p-5"
                    data-testid={`metric-${m.label.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <p className="font-display text-3xl md:text-4xl text-[#D4AF37] leading-none">
                      {m.value}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-display mt-3">
                      {m.label}
                    </p>
                  </div>
                ))}
              </div>

              {/* Process */}
              <div className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-4">
                  Creative Process
                </p>
                <ol className="space-y-2.5">
                  {p.process.map((step, i) => (
                    <li key={step} className="flex items-start gap-3 text-sm text-white/80">
                      <span className="w-6 h-6 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] flex items-center justify-center shrink-0 font-display">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Learnings */}
              <div className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-4">
                  Key Learnings
                </p>
                <ul className="space-y-2">
                  {p.learnings.map((l) => (
                    <li key={l} className="flex items-start gap-2 text-sm text-white/80">
                      <span className="text-[#D4AF37] mt-1">→</span>
                      {l}
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href="https://www.instagram.com/reel/DUX4NWOjMbq/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA=="
                target="_blank"
                rel="noreferrer"
                className="magnetic-btn"
                data-testid="featured-cta"
              >
                Watch the Series
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
